import React, { useState } from 'react';
import { Button, Searchbar, Select, Toolbar } from 'react-unity';
import styled from 'styled-components';
import PortOpeningRequest from '../../models/entities/PortOpeningRequest';
import { PortOpeningRequestsDirection } from '../../models/enums/POR/PortOpeningRequestsDirection';
import CheckboxFilter, { CheckboxOption } from '../common/tables/CheckboxFilter';
import Filter from '../common/tables/Filter';
import { useNavigate } from 'react-router-dom';
import { WorkflowState } from '../../models/enums/WorkflowState';

interface PORByRequestFilterProps {
	pors: PortOpeningRequest[];
	onFilterChange: any;
	subscriptionId: number;
	subscriptionOwner: string;
}

export type PORFilters = {
	scope: CheckboxOption[];
	status: CheckboxOption[];
	isAttestation: CheckboxOption[];
	searchUser: string;
	orderBy: string;
};

const MyP = styled.p`
	margin-right: 8px;
	margin-bottom: auto !important;
	margin-top: auto !important;
`;

const StyledItem = styled(Toolbar.Item)`
	width: 50%;
`;

const MyLink = styled.span`
	color: #0c69b0;
	text-decoration: underline;
	cursor: pointer;
`;

const PORByRequestFilter = ({ pors, onFilterChange, subscriptionId, subscriptionOwner }: PORByRequestFilterProps) => {
	const navigate = useNavigate();
	const getDefaultFilters = () => {
		const defaultFilters = {
			searchUser: '',
			status: [
				{ label: 'Submitted', value: 'Submitted', checked: true },
				{ label: 'Rejected', value: 'Rejected', checked: true },
				{
					label: 'Pending subscription owner approval',
					value: 'Pending subscription owner approval',
					checked: true,
				},
				{ label: 'Pending for DOAG Approval', value: 'Pending for DOAG Approval', checked: true },
				{
					label: 'Pending for Cloud Network Approval',
					value: 'Pending for Cloud Network Approval',
					checked: true,
				},
				{
					label: 'Pending Network Policy Solution Architecture approval',
					value: 'Pending Network Policy Solution Architecture approval',
					checked: true,
				},
				{
					label: 'Pending for Gateway Policy Team Approval',
					value: 'Pending for Gateway Policy Team Approval',
					checked: true,
				},
				{ label: 'Pending for Cyber Approval', value: 'Pending for Cyber Approval', checked: true },
				{
					label: 'Pending Subscription to Spoke Approval',
					value: 'Pending Subscription to Spoke Approval',
					checked: true,
				},
				{ label: 'Pending Attestation', value: 'Pending Attestation', checked: true },
				{ label: 'Review', value: 'Review', checked: true },
				{ label: 'Awaiting Implementation', value: 'Awaiting Implementation', checked: true },
				{ label: 'Implementation Failed', value: 'Implementation Failed', checked: true },
				{ label: 'Completed', value: 'Completed', checked: true },
				{ label: 'Cancelled', value: 'Cancelled', checked: true },
				{ label: 'Draft', value: 'Draft', checked: true },
			],
			scope: [
				{
					label: 'Subscription <-> ExxonMobil Network',
					value: PortOpeningRequestsDirection.SubscriptionToOnPrem.name,
					checked: true,
				},
				{
					label: 'Subscription -> Internet',
					value: PortOpeningRequestsDirection.SubscriptionToInternet.name,
					checked: true,
				},
				{
					label: 'Subscription -> Subscription',
					value: PortOpeningRequestsDirection.SubscriptionToSubscription.name,
					checked: true,
				},
			],
			isAttestation: [
				{
					label: 'Yes',
					value: 'true',
					checked: true,
				},
				{
					label: 'No',
					value: 'false',
					checked: true,
				}
			],
			orderBy: 'newest',
		} as PORFilters;

		return defaultFilters;
	};

	const filterPORs = () => {
		let filteredRequests = [];

		const filterCallback = (por: PortOpeningRequest) => {
			return (
				filter.filterCheckBox(
					'status',
					por.workflowInstance.currentWorkflowInstanceState.workflowState?.name
				) &&
				filter.filterCheckBox('scope', por.direction.name) &&
				filter.filterCheckBox('isAttestation', por.isAttestation?.toString()) &&
				(filter.filterSearchText('searchUser', por.workflowInstance.createdBy.displayName) ||
				filter.filterSearchText('searchUser', por.contactUser?.displayName) ||
				filter.filterSearchText('searchUser', por.id.toLocaleString()) || (
				por.workflowInstance.currentWorkflowInstanceState.workflowState.value ===
					WorkflowState.PendingSubscriptionOwnerApproval.value
					? filter.filterSearchText('searchUser', subscriptionOwner)
					: filter.filterSearchText('searchUser', por.approvingUser?.displayName)))
			);
		};

		filteredRequests = pors.filter(filterCallback).sort((a, b) => {
			if (filter.filters.orderBy === 'newest') {
				return a.id < b.id ? 1 : -1;
			}
			return a.id > b.id ? 1 : -1;
		});
		onFilterChange(filteredRequests);
	};
	const [filter] = useState<Filter<PORFilters>>(new Filter(filterPORs, getDefaultFilters()));

	const clearSearchbar = (e) => {
		e.preventDefault();
		filter.updateFilter('', 'searchUser');
	};

	return (
		<>
			{filter && (
				<>
					<Toolbar>
						<StyledItem>
							<Searchbar
								condensed
								buttonProps={{ onClick: clearSearchbar}}
								inputProps={{
									value: filter.filters.searchUser,
									onChange: (e) => filter.handleSearchTextChange(e, 'searchUser'),
									placeholder: 'Search all fields shown below (i.e. specific IP address, Description, etc.)',
								}}
							/>
						</StyledItem>
						<Toolbar.Item right>
							<Button.Group>
								<Button
									variant="primary"
									size="small"
									onClick={() => {
										navigate(`/subscriptions/${subscriptionId}/portOpeningRequest/new`);
									}}
								>
									<span>Open new request</span>
								</Button>
							</Button.Group>
						</Toolbar.Item>
					</Toolbar>
					<Toolbar>
						<Toolbar.Item>
							<CheckboxFilter
								label="Scope"
								buttonProps={{ size: 'small' }}
								onChange={(event) => filter.handleCheckboxChange(event, 'scope')}
								options={filter.filters.scope}
							/>
						</Toolbar.Item>
						<Toolbar.Item>
							<CheckboxFilter
								label="Status"
								onChange={(event) => filter.handleCheckboxChange(event, 'status')}
								options={filter.filters.status}
							/>
						</Toolbar.Item>
						<Toolbar.Item>
							<CheckboxFilter
								label="Attestation"
								onChange={(event) => filter.handleCheckboxChange(event, 'isAttestation')}
								options={filter.filters.isAttestation}
							/>
						</Toolbar.Item>
						<Toolbar.Item>
							<MyLink onClick={() => filter.updateFilters(getDefaultFilters())}>Clear filters</MyLink>
						</Toolbar.Item>
						<Toolbar.Item right>
							<Button.Group>
								<MyP>Sort by</MyP>
								<Select
									style={{ marginBottom: -12 }}
									value={filter.filters.orderBy}
									onChange={(e) => filter.handleSearchTextChange(e, 'orderBy')}
								>
									<option className='em-c-field-option' value="newest">Newest</option>
									<option className='em-c-field-option' value="oldest">Oldest</option>
								</Select>
							</Button.Group>
						</Toolbar.Item>
					</Toolbar>
				</>
			)}
		</>
	);
};

export default PORByRequestFilter;
